import { useEffect, useCallback, useRef, useContext } from "react";
import { differenceInMinutes, differenceInSeconds, getTime } from "date-fns";
import { AuthContext } from "@/guards/auth/auth-context";
import { LocalStorageKey, SESSION_TIMEOUT } from "@/constants";
import { toast } from "@/components";
import { appConfig } from "@/guards/auth/azure-b2c.config";

export const SessionTimeout = () => {
  const { account, logout } = useContext(AuthContext);
  const warningInactiveInterval = useRef<number | null>(null);
  const toastIdRef = useRef<string | null>(null);

  const warningInactive = useCallback(
    (timeString: string) => {
      if (toastIdRef.current) {
        toast.remove(toastIdRef.current);
        toastIdRef.current = null;
      }

      warningInactiveInterval.current = window.setInterval(() => {
        const maxTime = appConfig?.FEATURES?.IS_DEBUG_ENABLED
          ? SESSION_TIMEOUT * 10000
          : SESSION_TIMEOUT;

        const timeNow = new Date();
        const diffMinutes = differenceInMinutes(timeNow, +timeString);

        const secondsLeft = differenceInSeconds(
          timeNow,
          +timeString + maxTime * 60000,
        );

        if (diffMinutes === maxTime - 1) {
          const toastMsg = (
            <>
              <p>Your session will expire in {secondsLeft * -1} seconds.</p>
              <p>Click any way to continue</p>
            </>
          );

          if (toastIdRef.current) {
            toastIdRef.current = toast.error(toastMsg, {
              id: toastIdRef.current,
            });
          } else {
            toastIdRef.current = toast.error(toastMsg);
          }
        }

        if (diffMinutes === maxTime && logout) {
          if (warningInactiveInterval.current) {
            clearInterval(warningInactiveInterval.current);
          }

          sessionStorage.removeItem(LocalStorageKey.SessionKey);

          logout();
        }
      }, 1000);
    },
    [logout],
  );

  const timeChecker = useCallback(() => {
    const storedTimeStamp = sessionStorage.getItem(LocalStorageKey.SessionKey);

    if (storedTimeStamp) {
      warningInactive(storedTimeStamp);
    }
  }, [warningInactive]);

  const destroyTimer = useCallback(() => {
    if (warningInactiveInterval.current) {
      clearInterval(warningInactiveInterval.current);
    }

    if (toastIdRef.current) {
      toast.remove(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    destroyTimer();

    if (account) {
      sessionStorage.setItem(
        LocalStorageKey.SessionKey,
        `${getTime(new Date())}`,
      );
    } else {
      sessionStorage.removeItem(LocalStorageKey.SessionKey);
    }

    timeChecker();
  }, [account, destroyTimer, timeChecker]);

  useEffect(() => {
    const events = ["click", "load", "scroll"];

    sessionStorage.setItem(
      LocalStorageKey.SessionKey,
      `${getTime(new Date())}`,
    );

    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    timeChecker();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer, timeChecker]);

  return null;
};
